/**
 * render-cost-candidates — 레퍼런스 패널(화면 이름 '레퍼런스' — 매입 후보 목록, 순수 HTML). 판매단가 칸을 편집할 때 그 아래에 자동으로,
 *   또는 판매단가 칸의 ⓘ 로 수동으로 연다. "이 가격이면 어느 소스로 사도 얼마 남나"를 한눈에.
 *   줄마다 [재고|RFQ] 어디서 · 매입 ₩ · 입력가 기준 마진율 · [선택]. 지금 붙은 소스는 굵게 + '● 현재'.
 *   [선택]은 소스 팝오버의 선택과 같은 저장(quote_item 의 source_type·source_ref_id·cost_basis) — 버튼 dataset 모양도 같다.
 *   아래엔 목표 마진(20·25·30%)에 필요한 판매가 — 클릭하면 입력창에 채운다(확정 아님).
 */
import { type CostCandidate, SOURCE_TYPE_LABELS, marginPercentAt, sellUnitKrwAtMargin, lowestCostUnitKrw } from './paper-1-specs';

const esc = (value: unknown) => String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const won = (amount: number) => amount.toLocaleString('ko-KR');

/** 패널 살림 — View 의 this.data.costHint 그대로(null 이면 닫힘). 어느 품목 · 지금 입력 중인 판매 단가. */
export interface CostHintState { itemIndex: number; sellUnitKrw: number | null }

const TARGET_MARGIN_PERCENTS = [20, 25, 30];

function renderCandidateRows(candidates: CostCandidate[], sellUnitKrw: number | null, linkedSourceRefId: string | null, optionPart: string): string {
  return candidates.map((candidate) => {
    const isLinked = candidate.sourceRefId === linkedSourceRefId;
    const percent = marginPercentAt(sellUnitKrw, candidate.costUnitKrw);
    const percentClass = percent == null ? 'qc-none' : percent >= 20 ? 'qc-good' : percent >= 12 ? 'qc-mid' : 'qc-low';
    return `<div class="qc-row${isLinked ? ' qc-linked' : ''}">
      <span class="qc-kind qc-${candidate.sourceType === 'stock' ? 'stock' : 'rfq'}">${SOURCE_TYPE_LABELS[candidate.sourceType]}</span>
      <span class="qc-label">${esc(candidate.label)}</span>
      <span class="qc-cost">${candidate.costUnitKrw == null ? '매입 —' : `₩${won(candidate.costUnitKrw)}`}</span>
      <span class="qc-pct ${percentClass}">${percent == null ? '—' : `${percent}%`}</span>
      <button class="qc-pick${isLinked ? ' qc-cur' : ''}" type="button" data-action="qe-cost-hint-pick"${isLinked ? ' disabled' : ''}
        data-type="${candidate.sourceType}" data-ref="${esc(candidate.sourceRefId)}" data-amt="${candidate.costAmount ?? ''}" data-cur="${esc(candidate.costCurrency ?? '')}"
        data-part="${esc(optionPart)}">${isLinked ? '● 현재' : '선택'}</button>
    </div>`;
  }).join('');
}

/** 목표 마진 줄 — 기준 매입가(붙은 소스, 없으면 최저 후보)로 20·25·30% 를 내려면 판매가 얼마. 기준이 없으면 줄 없음. */
function renderTargetMargins(baseCostUnitKrw: number | null): string {
  if (baseCostUnitKrw == null) return '';
  const buttons = TARGET_MARGIN_PERCENTS.map((percent) => {
    const sell = sellUnitKrwAtMargin(baseCostUnitKrw, percent);
    return `<button class="qc-target" type="button" data-action="qe-cost-hint-fill" data-value="${sell}">${percent}% → ₩${won(sell)}</button>`;
  }).join('');
  return `<div class="qc-targets"><span class="qc-targets-k">목표 마진</span>${buttons}</div>`;
}

/**
 * 레퍼런스 패널 — 머리(건수) · 후보 줄들(많으면 이 부분만 스크롤) · 목표 마진(고정).
 * baseCostUnitKrw = 목표 마진의 기준 매입가(붙은 소스의 매입 단가, 없으면 최저 후보). isLoaded 가 false 면 아직 조회 중.
 */
export function renderCostCandidatesPanel(
  candidates: CostCandidate[], sellUnitKrw: number | null, linkedSourceRefId: string | null,
  linkedCostUnitKrw: number | null, isLoaded: boolean,
  optionPart: string,   // 이 품목의 파트넘버 — [선택] 저장 몸통(sourcePickPatch)이 같은 파트인지 대조
): string {
  const body = !isLoaded
    ? `<div class="qc-empty">레퍼런스를 조회하고 있습니다…</div>`
    : candidates.length === 0
      ? `<div class="qc-empty">이 파트넘버의 재고·RFQ 후보가 없습니다. 소스 칸에서 대체품을 검색하세요.</div>`
      : renderCandidateRows(candidates, sellUnitKrw, linkedSourceRefId, optionPart);
  const baseCostUnitKrw = linkedCostUnitKrw ?? lowestCostUnitKrw(candidates);
  return `<div class="qc-panel" role="dialog">
    <div class="qc-h"><span>레퍼런스 <span class="qc-sub">${isLoaded ? `${candidates.length}건 · ` : ''}입력가 기준 마진율</span></span><button class="qc-close" type="button" data-action="qe-cost-hint-close" title="닫기">✕</button></div>
    <div class="qc-body">${body}</div>
    ${isLoaded ? renderTargetMargins(baseCostUnitKrw) : ''}
  </div>`;
}

export const COST_CANDIDATES_CSS = `
.qe-cost-hint-slot{position:absolute;z-index:40}
.qc-panel{width:360px;background:#fff;border:1px solid #cfd8e0;border-radius:10px;box-shadow:0 12px 32px rgba(10,25,35,.22);font:12px 'Malgun Gothic','맑은 고딕',system-ui,sans-serif;color:#26323b}
.qc-h{display:flex;justify-content:space-between;align-items:center;padding:8px 12px;border-bottom:1px solid #eef1f4;font-weight:700;font-size:12.5px}
.qc-sub{font-weight:400;color:#95a0a9;font-size:11px;margin-left:6px}
.qc-close{border:0;background:none;color:#9aa7b1;cursor:pointer;font-size:12px}
.qc-body{max-height:238px;overflow-y:auto;overscroll-behavior:contain}   /* 후보가 많으면 여기만 스크롤 — 머리·목표 마진은 고정 */
.qc-row{display:grid;grid-template-columns:auto 1fr auto 44px auto;gap:10px;align-items:center;padding:6px 12px;border-bottom:1px solid #f4f6f8}
.qc-row.qc-linked .qc-label,.qc-row.qc-linked .qc-cost{font-weight:700}
.qc-pick{font:600 11px system-ui;background:#fff;border:1px solid #cdd8ea;border-radius:6px;padding:3px 8px;color:#2f6fed;cursor:pointer;white-space:nowrap}
.qc-pick:hover{background:#eef4ff}
.qc-pick.qc-cur{color:#1a7d4d;border-color:#bfe3cd;background:#eaf5ee;cursor:default}
.qc-kind{font:700 10px system-ui;padding:2px 6px;border-radius:4px}
.qc-stock{background:#eaf5ee;color:#1a7d4d}.qc-rfq{background:#eef2fe;color:#3a5bd0}
.qc-label{color:#3a4650;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.qc-cost{font-variant-numeric:tabular-nums;text-align:right}
.qc-pct{font-weight:700;text-align:right;font-variant-numeric:tabular-nums}
.qc-good{color:#1a9d63}.qc-mid{color:#c9821b}.qc-low{color:#d0453b}.qc-none{color:#c4ccd2}
.qc-empty{padding:14px 12px;color:#95a0a9;text-align:center;line-height:1.5}
.qc-targets{display:flex;align-items:center;gap:6px;flex-wrap:wrap;padding:8px 12px;background:#fafbfc;border-top:1px solid #eef1f4;border-radius:0 0 10px 10px}
.qc-targets-k{font-size:11px;color:#8a97a1;margin-right:2px}
.qc-target{font:600 11px system-ui;background:#fff;border:1px solid #cdd8ea;border-radius:6px;padding:4px 8px;color:#2f6fed;cursor:pointer;white-space:nowrap}
.qc-target:hover{background:#eef4ff}
`;
